// src/stores/auth.js
import { defineStore } from 'pinia';
import axios from 'axios';

const api = axios.create({
    baseURL: 'https://eisenbahn.wingen.de/api/user',
    headers: { 'Content-Type': 'application/json' },
});

let getToken = null;
export function bindTokenGetter(fn) { getToken = fn; }
let onUnauthorized = null;
export function bindUnauthorizedHandler(fn) { onUnauthorized = fn; }

api.interceptors.request.use((config) => {
    const token = getToken && getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});
api.interceptors.response.use(
    (res) => res,
    (err) => {
        const status  = err?.response?.status;
        const hadAuth = !!err?.config?.headers?.Authorization;
        if (status === 401 && hadAuth && onUnauthorized) {
            onUnauthorized();
        }
        return Promise.reject(err);
    }
);

const STORAGE_KEY = 'auth.v1';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: null,
        tokenExpiresAt: null,
        email: null,
        loading: false,
        error: null,
        access: { checkedAt: null, active: null, grant: null },
        ui: {
            loginVisible: false,
            loginRedirect: null,
            loginTitle: 'Anmelden',
            prefillEmail: null,
        },
    }),

    getters: {
        isAuthenticated: (s) => !!s.token,
        hasActiveAccess: (s) => s.access.active === true,
        accessUntil: (s) => (s.access.grant ? s.access.grant.ends_at : null),
    },

    actions: {
        viewLogin(opts) {
            this.ui.loginTitle = (opts && opts.title) || 'Anmelden';
            this.ui.loginRedirect = (opts && opts.redirect) || null;
            this.ui.prefillEmail = (opts && opts.email) || null;
            this.ui.loginVisible = true;
        },
        hideLogin() {
            this.ui.loginVisible = false;
            this.ui.loginRedirect = null;
            this.ui.prefillEmail = null;
        },

        loadFromStorage() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                if (!raw) return;
                const data = JSON.parse(raw);
                this.token = data.token || null;
                this.tokenExpiresAt = data.tokenExpiresAt || null;
                this.email = data.email || null;
                this.access = data.access || this.access;
            } catch {}
        },
        saveToStorage() {
            const payload = {
                token: this.token,
                tokenExpiresAt: this.tokenExpiresAt,
                email: this.email,
                access: this.access,
            };
            localStorage.setItem('auth.v1', JSON.stringify(payload));
            console.log('[auth] saved', payload); // DEBUG
        },

        normalizeError(err) {
            const apiMsg = err?.response?.data?.error;
            if (apiMsg) return apiMsg;
            if (err?.message) return err.message;
            return 'Unbekannter Fehler';
        },

        isTokenExpired() {
            if (!this.tokenExpiresAt) return true;
            return Date.now() >= Date.parse(this.tokenExpiresAt);
        },

        init({ validateOnStart = false } = {}) {
            this.loadFromStorage();
            bindTokenGetter(() => this.token);
            bindUnauthorizedHandler(() => { if (this.token) this.logout({ silentServer: true }); });

            if (validateOnStart && this.token) {
                this.fetchAccessMe().catch(() => {/* soft-fail, UI bleibt, Interceptor handled 401 */});
            }
            if (this.token && this.isTokenExpired()) {
                this.logout({ silentServer: true }); // Token ist clientseitig abgelaufen
            }
        },

        async register(email, password) {
            this.loading = true; this.error = null;
            try {
                await api.post('/register', { email, password });
                return true;
            } catch (err) {
                this.error = this.normalizeError(err);
                return false;
            } finally { this.loading = false; }
        },

        async login(email, password) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/login', { email, password });
                if (!data?.ok || !data?.token) throw new Error('Login fehlgeschlagen');

                this.token = data.token;
                this.tokenExpiresAt = data.expires_at || null;
                this.email = email;

                // DEBUG: vor Persist überprüfen
                console.log('[auth] login token', this.token);

                this.saveToStorage(); // <-- MUSS hier passieren

                // Optional: Header für den allerersten Call sofort setzen
                // (falls der Interceptor erst beim nächsten Request greift)
                api.defaults.headers.common.Authorization = `Bearer ${this.token}`;
                return true;
            } catch (err) {
                this.error = this.normalizeError(err);
                this.token = null;
                this.tokenExpiresAt = null;
                this.saveToStorage();
                return false;
            } finally {
                this.loading = false;
            }
        },

        async logout(opts = {}) {
            this.loading = true; this.error = null;
            try {
                if (!opts.silentServer) await api.post('/logout', {});
            } catch {}
            this.token = null;
            this.tokenExpiresAt = null;
            this.email = null;
            this.access = { checkedAt: null, active: null, grant: null };
            this.saveToStorage();
            this.loading = false;
        },

        async fetchAccessMe() {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.get('/access/me', {
                    headers: { Authorization: `Bearer ${this.token}` } // <-- wichtig beim ersten Call
                });
                this.access.checkedAt = new Date().toISOString();
                this.access.active = !!data?.active;
                this.access.grant = data?.grant || null;
                this.saveToStorage();
                return { active: this.access.active, grant: this.access.grant };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { active: false, grant: null };
            } finally { this.loading = false; }
        },
    },
});
